import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CHAT_CLOSE_SESSION_EXPIRED, chatSocketUrl } from "../api/chat";
import type { ChatSocketEvent } from "../api/chat";
import {
  chatQueryKeys,
  markMessageDeleted,
  mergeMessage,
} from "../queries/chat";

export type ChatConnectionStatus = "connecting" | "open" | "offline";

type ChatSocketValue = {
  status: ChatConnectionStatus;
  /** Users currently typing, keyed by conversation id. */
  typingByConversation: Record<string, string[]>;
  sendTyping: (conversationId: string, typing: boolean) => void;
  /** Forces an immediate reconnect, used by the manual retry affordance. */
  reconnect: () => void;
};

const ChatSocketContext = createContext<ChatSocketValue>({
  status: "offline",
  typingByConversation: {},
  sendTyping: () => {},
  reconnect: () => {},
});

export const useChatSocket = () => useContext(ChatSocketContext);

/** Backoff schedule in milliseconds, with the last value repeating. */
const BACKOFF_STEPS = [1_000, 2_000, 5_000, 10_000, 30_000];

/** A typing signal older than this is dropped, in case a stop frame is lost. */
const TYPING_TIMEOUT_MS = 6_000;

function backoffDelay(attempt: number): number {
  const base = BACKOFF_STEPS[Math.min(attempt, BACKOFF_STEPS.length - 1)];
  // Jitter keeps many reconnecting tabs from hitting the API in lockstep.
  return base + Math.floor(Math.random() * 500);
}

type ChatSocketProviderProps = {
  enabled: boolean;
  children: React.ReactNode;
};

/**
 * Owns the single delivery socket for the whole app.
 *
 * One connection is shared across every surface, so the dock, the inbox and
 * the unread badges all observe the same stream rather than opening a socket
 * each.
 */
export function ChatSocketProvider({
  enabled,
  children,
}: ChatSocketProviderProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ChatConnectionStatus>("offline");
  const [typingByConversation, setTypingByConversation] = useState<
    Record<string, string[]>
  >({});

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  // Set when the API closes the socket for an expired session, which must not
  // be retried: reconnecting would loop against a credential that is gone.
  const stoppedRef = useRef(false);

  const clearTypingFor = useCallback(
    (conversationId: string, userId: string) => {
      setTypingByConversation((current) => {
        const existing = current[conversationId];
        if (!existing?.includes(userId)) return current;
        const remaining = existing.filter((id) => id !== userId);
        const next = { ...current };
        if (remaining.length) {
          next[conversationId] = remaining;
        } else {
          delete next[conversationId];
        }
        return next;
      });
    },
    [],
  );

  const handleEvent = useCallback(
    (event: ChatSocketEvent) => {
      switch (event.event) {
        case "message_created":
          mergeMessage(queryClient, event.conversation_id, event.message);
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.conversations,
          });
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.unread,
          });
          // A message from someone ends their typing indicator.
          clearTypingFor(event.conversation_id, event.message.sender_id);
          break;
        case "message_deleted":
          markMessageDeleted(
            queryClient,
            event.conversation_id,
            event.message_id,
          );
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.conversations,
          });
          break;
        case "conversation_created":
        case "conversation_updated":
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.conversations,
          });
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.conversation(event.conversation_id),
          });
          break;
        case "conversation_read":
          // Another tab of this user read the thread, so badges follow.
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.conversations,
          });
          void queryClient.invalidateQueries({
            queryKey: chatQueryKeys.unread,
          });
          break;
        case "typing": {
          const { conversation_id: conversationId, user_id: userId } = event;
          const timerKey = `${conversationId}:${userId}`;
          const existingTimer = typingTimersRef.current[timerKey];
          if (existingTimer) clearTimeout(existingTimer);

          if (!event.typing) {
            clearTypingFor(conversationId, userId);
            break;
          }
          setTypingByConversation((current) => {
            const existing = current[conversationId] || [];
            if (existing.includes(userId)) return current;
            return { ...current, [conversationId]: [...existing, userId] };
          });
          typingTimersRef.current[timerKey] = setTimeout(
            () => clearTypingFor(conversationId, userId),
            TYPING_TIMEOUT_MS,
          );
          break;
        }
        case "pong":
        default:
          break;
      }
    },
    [clearTypingFor, queryClient],
  );

  const connect = useCallback(() => {
    if (!enabled || stoppedRef.current) return;
    if (socketRef.current) return;

    setStatus("connecting");
    let socket: WebSocket;
    try {
      socket = new WebSocket(chatSocketUrl());
    } catch {
      // A malformed URL or a blocked scheme is not retryable in a tight loop.
      setStatus("offline");
      return;
    }
    socketRef.current = socket;

    socket.onopen = () => {
      attemptRef.current = 0;
      setStatus("open");
      // Live frames only cover what happens from now on, so anything missed
      // while disconnected is picked up by refetching.
      void queryClient.invalidateQueries({ queryKey: chatQueryKeys.all });
    };

    socket.onmessage = (message) => {
      try {
        handleEvent(JSON.parse(message.data) as ChatSocketEvent);
      } catch {
        // A frame this build does not understand is ignored rather than
        // treated as a connection failure.
      }
    };

    socket.onclose = (event) => {
      socketRef.current = null;
      setStatus("offline");
      setTypingByConversation({});

      if (event.code === CHAT_CLOSE_SESSION_EXPIRED) {
        stoppedRef.current = true;
        void queryClient.cancelQueries();
        navigate("/login", { replace: true });
        return;
      }
      if (!enabled || stoppedRef.current) return;

      const delay = backoffDelay(attemptRef.current);
      attemptRef.current += 1;
      retryTimerRef.current = setTimeout(connect, delay);
    };

    socket.onerror = () => {
      // The close handler owns retry scheduling, so this only needs to make
      // sure the socket is actually torn down.
      socket.close();
    };
  }, [enabled, handleEvent, navigate, queryClient]);

  const reconnect = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    attemptRef.current = 0;
    stoppedRef.current = false;
    if (socketRef.current) {
      socketRef.current.close();
      return;
    }
    connect();
  }, [connect]);

  useEffect(() => {
    if (!enabled) {
      stoppedRef.current = false;
      return undefined;
    }
    connect();
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      Object.values(typingTimersRef.current).forEach(clearTimeout);
      typingTimersRef.current = {};
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        // Detached first so the teardown does not schedule a reconnect.
        socket.onclose = null;
        socket.close();
      }
    };
  }, [connect, enabled]);

  const sendTyping = useCallback((conversationId: string, typing: boolean) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(
      JSON.stringify({
        action: "typing",
        conversation_id: conversationId,
        typing,
      }),
    );
  }, []);

  const value = useMemo(
    () => ({ status, typingByConversation, sendTyping, reconnect }),
    [status, typingByConversation, sendTyping, reconnect],
  );

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
}
